import React from 'react';
import { Box, Flex } from '@fullstory/components';
import { Filter } from './types';

export function getFiltersElement<R>(filters: Filter<R>[]) {
  return (
    <Flex data-component="BulkActionTableGetFiltersElement" justifyContent="flex-start">
      {filters.map(f => (
        <Box key={f.key} marginRight={8}>
          {f.render()}
        </Box>
      ))}
    </Flex>
  );
}

/**
 * Hook to filter a set of rows and render the UI for a given set of filters.
 * @param rows The full set of rows to be filtered.
 * @param filters The set of filters to render and apply.
 */
export function useFilters<R>(rows: R[], filters: Filter<R>[]) {
  const [filteredRows, setFilteredRows] = React.useState<R[]>([]);

  React.useEffect(() => {
    const getFilteredRows = async () => {
      // Kick off async filters
      const filteredRows = await Promise.all(filters.map(f => f.filterRows(rows)));
      // Map filtered rows to Sets so we can intersect them all
      const filteredSets = filteredRows.map(rows => new Set(rows));
      // The final set of rows will be the intersection of all individual filters
      const result = rows.filter(row => filteredSets.every(f => f.has(row)));
      setFilteredRows(result);
    };
    getFilteredRows();
  }, [rows, filters]);

  return { rows: filteredRows, filters: getFiltersElement(filters) };
}
